import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, BoxesIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
type Batch = {
  arrived: string;
  name: string;
  received: number;
  msrp?: number;
  estimatedRecovery?: number;
  lineItems?: number;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const onePeopleIncomingBatches = [{
  arrived: 'Feb 5, 2026',
  name: 'Spring overstock',
  expected: 1365,
  received: 1267,
  progress: 92.8
}, {
  arrived: 'Dec 6, 2025',
  name: 'Assembly part 4',
  expected: 7,
  received: 7,
  progress: 100
}, {
  arrived: 'Aug 30, 2025',
  name: 'August returns',
  expected: 337,
  received: 335,
  progress: 99.4
}, {
  arrived: 'Jul 10, 2025',
  name: 'July overstock',
  expected: 16,
  received: 16,
  progress: 100
}, {
  arrived: 'May 2, 2025',
  name: 'May overstock',
  expected: 258,
  received: 258,
  progress: 100
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
const eddyIncomingBatches = [{
  arrived: 'Nov 4, 2026',
  name: 'Eddy — January 3PL transfer',
  expected: 85,
  received: 86,
  progress: 100
}, {
  arrived: 'Jan 28, 2026',
  name: 'Eddy Samples',
  expected: 0,
  received: 0,
  progress: 0
}, {
  arrived: 'Dec 16, 2025',
  name: 'Eddy — December 2025 3PL transfer',
  expected: 1103,
  received: 1105,
  progress: 100
}, {
  arrived: 'Nov 21, 2025',
  name: 'Eddy 3PL transfer',
  expected: 118,
  received: 120,
  progress: 100
}, {
  arrived: 'Oct 10, 2025',
  name: 'Eddy 3PL Transfer',
  expected: 87,
  received: 86,
  progress: 100
}, {
  arrived: 'May 22, 2025',
  name: 'Eddy — TJX Overflow',
  expected: 0,
  received: 223,
  progress: 97.4
}, {
  arrived: 'May 19, 2025',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 845,
  progress: 99.7
}, {
  arrived: 'Sep 30, 2024',
  name: 'Eddy — October 3PL Transfer',
  expected: 22,
  received: 25,
  progress: 100
}, {
  arrived: 'Aug 13, 2024',
  name: 'Eddy — Fall 2024',
  expected: 168,
  received: 169,
  progress: 100
}, {
  arrived: 'Apr 8, 2024',
  name: 'Eddy April 2024',
  expected: 0,
  received: 0,
  progress: 100
}, {
  arrived: 'Jan 18, 2024',
  name: 'Eddy — Jan 2024',
  expected: 5,
  received: 0,
  progress: 100
}, {
  arrived: 'Dec 20, 1905',
  name: 'Eddy — 3PL transfer old',
  expected: 0,
  received: 183,
  progress: 100
}, {
  arrived: '—',
  name: 'Eddy — June TRX',
  expected: 139,
  received: 139,
  progress: 98.1
}, {
  arrived: '—',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 0,
  progress: 0
}];
type BrandBase = {
  receivedProducts: number;
  receivedUnits: number;
  totalMsrp: number;
  maxOpportunity: number;
  soldUnits: number;
  totalSales: number;
};
type BatchRow = Batch & {
  brand: string;
  id: string;
};
type SortKey = 'uploaded' | 'batch' | 'brand' | 'units' | 'msrp' | 'recovery';
const ROWS_PER_PAGE = 50;

// Submission dates arrive as display strings ("Feb 5, 2026") and can be missing.
function uploadedTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
const onePeopleBase: BrandBase = {
  receivedProducts: 189,
  receivedUnits: 1902,
  totalMsrp: 130993,
  maxOpportunity: 65437,
  soldUnits: 2920,
  totalSales: 49411
};
const eddyBase: BrandBase = {
  receivedProducts: 320,
  receivedUnits: 2981,
  totalMsrp: 321656,
  maxOpportunity: 160828,
  soldUnits: 4010,
  totalSales: 325875
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const nisoloActive = includes('Nisolo');
  const {
    inventory: nisolo
  } = useNisoloProducts(nisoloActive);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(nisoloActive);
  const {
    data: nisoloOrders
  } = useNisoloOrders(nisoloActive);
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: 'asc' | 'desc';
  }>({
    key: 'uploaded',
    direction: 'desc'
  });
  const [page, setPage] = useState(1);
  useEffect(() => setDeselected([]), [selectedOwner]);
  useEffect(() => setPage(1), [selectedOwner, sort]);
  const toggleSort = (key: SortKey) => setSort(current => current.key === key ? {
    key,
    direction: current.direction === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    direction: key === 'batch' || key === 'brand' ? 'asc' : 'desc'
  });
  const brands = useMemo(() => {
    const entries: {
      brand: string;
      batches: Batch[];
      base: BrandBase;
    }[] = [];
    if (includes('1 People')) entries.push({
      brand: '1 People',
      batches: onePeopleIncomingBatches,
      base: onePeopleBase
    });
    if (includes('Eddy')) entries.push({
      brand: 'Eddy',
      batches: eddyIncomingBatches,
      base: eddyBase
    });
    if (nisoloActive) {
      entries.push({
        brand: 'Nisolo',
        batches: nisoloSubmissions?.submissions ?? [],
        base: {
          receivedProducts: nisolo?.totalProducts ?? 0,
          receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
          totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
          maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
          soldUnits: nisoloOrders?.units ?? 0,
          totalSales: nisoloOrders?.sales ?? 0
        }
      });
    }
    return entries;
  }, [selectedOwner, nisolo, nisoloSubmissions, nisoloOrders]);
  const unsortedRows: BatchRow[] = useMemo(
  // Batch names repeat within a brand, so the index keeps each row's id unique.
  () => brands.flatMap(({
    brand,
    batches
  }) => batches.map((batch, index) => ({
    ...batch,
    brand,
    id: `${brand}::${index}::${batch.name}`
  }))), [brands]);
  const rows: BatchRow[] = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const value = (row: BatchRow) => {
      switch (sort.key) {
        case 'uploaded':
          return uploadedTime(row.arrived);
        case 'batch':
          return row.name.toLocaleLowerCase();
        case 'brand':
          return row.brand.toLocaleLowerCase();
        case 'units':
          return row.received;
        case 'msrp':
          return row.msrp ?? 0;
        case 'recovery':
          return row.estimatedRecovery ?? 0;
      }
    };
    return [...unsortedRows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [unsortedRows, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const isSelected = (row: BatchRow) => !deselected.includes(row.id);
  const toggleBatch = (row: BatchRow) => setDeselected(current => current.includes(row.id) ? current.filter(id => id !== row.id) : [...current, row.id]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? rows.map(row => row.id) : []);

  // Each brand contributes only its selected submissions. Brands whose batch
  // records carry no valuation fall back to a share of their known totals.
  const totals = useMemo(() => {
    return brands.reduce((sum, {
      brand,
      batches,
      base
    }) => {
      const selected = batches.filter((batch, index) => !deselected.includes(`${brand}::${index}::${batch.name}`));
      const brandUnits = batches.reduce((count, batch) => count + batch.received, 0);
      const selectedUnits = selected.reduce((count, batch) => count + batch.received, 0);
      const share = batches.length === 0 ? 1 : brandUnits > 0 ? selectedUnits / brandUnits : selected.length / batches.length;
      const scale = (value: number) => Math.round(value * share);
      const lines = selected.reduce((count, batch) => count + (batch.lineItems ?? 0), 0);
      const batchMsrp = selected.reduce((value, batch) => value + (batch.msrp ?? 0), 0);
      const batchRecovery = selected.reduce((value, batch) => value + (batch.estimatedRecovery ?? 0), 0);
      const matched = brand === 'Nisolo' ? selected.reduce((result, submission) => {
        const outcome = reconcileSubmission(submission, nisoloOrders);
        return {
          units: result.units + outcome.soldUnits,
          sales: result.sales + outcome.actualSales
        };
      }, {
        units: 0,
        sales: 0
      }) : {
        units: scale(base.soldUnits),
        sales: scale(base.totalSales)
      };
      return {
        products: sum.products + (lines > 0 ? lines : scale(base.receivedProducts)),
        lines: sum.lines + lines,
        units: sum.units + (batches.length > 0 ? selectedUnits : base.receivedUnits),
        msrp: sum.msrp + (batchMsrp > 0 ? batchMsrp : scale(base.totalMsrp)),
        recovery: sum.recovery + (batchRecovery > 0 ? batchRecovery : scale(base.maxOpportunity)),
        soldUnits: sum.soldUnits + matched.units,
        totalSales: sum.totalSales + matched.sales
      };
    }, {
      products: 0,
      lines: 0,
      units: 0,
      msrp: 0,
      recovery: 0,
      soldUnits: 0,
      totalSales: 0
    });
  }, [brands, deselected, nisoloOrders]);
  const receivedProducts = totals.products;
  const receivedUnits = totals.units;
  const selectedLines = totals.lines;
  const predictedRecovery = totals.recovery;
  const soldUnits = totals.soldUnits;
  const totalSales = totals.totalSales;
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }, {
    label: 'Units sold',
    value: soldUnits.toLocaleString('en-US'),
    detail: 'Matched to selected uploads'
  }, {
    label: 'Total sales',
    value: currency(totalSales),
    detail: 'Gross recovery revenue'
  }, {
    label: 'Actual recovery / unit',
    value: perUnit(totalSales, soldUnits),
    detail: soldUnits > 0 ? 'Per unit sold' : 'No sales yet'
  }];
  const msrpValue = totals.msrp;
  const totalMsrp = currency(msrpValue);
  const maxOpportunity = currency(predictedRecovery);
  const totalReceivedValue = currency(msrpValue);
  const overstockDetail = `${receivedUnits.toLocaleString('en-US')} units across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1020" data-mp="1i7urw4o94s1l">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1021" data-mp="1n3a4yuefqjwy">
        <div data-id="element-1022" data-mp="1w1vqmyf7ef1y">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1023" data-mp="1w2do1ydlqzbg">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1024" data-mp="211gqrc640tg9" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1025" data-mp="3lkarulh997vz">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1026" data-mp="3ch9p8urllyx2">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1027" data-mp="831pwdgu3nw0">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1028" data-mp="3vgnmpmojdrsb" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1029" data-mp="3qh19hd8pkbji">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1030" data-mp="n0c43ukv37vf">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1031" data-mp="q9u52vqstyd6">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1032" data-mp="qaveyww2wetb" />
        </div> : <>
      <section className="mt-6 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-1033" data-mp="u9vv27n693cm">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1034" data-mp="gizcl9mym2ja">
          <div className="flex items-center gap-2" data-id="element-1035" data-mp="lgr9yj6diguv">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1036" data-mp="197esdydx0ovo" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1037" data-mp="1dzofl6nnbau9">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-1038" data-mp="1ndgfnxl5ts2a">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-1039" data-mp="1jbbrhedhdnn1" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1040" data-mp="1s5q1p2j5m7za">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1041" data-mp="14eue6hlqdyxu">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1042" data-mp="10e0p3kwjye3d">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-1043" data-mp="1eev08bazit5q">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-1044" data-mp="2ps6wa00i67hc">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-1045" data-mp="2pujor73w008n" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-1046" data-mp="2kskklv0bozan">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-1047" data-mp="2sts9lpldddq2">
            <div data-id="element-1048" data-mp="2wuvly247krmj">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1049" data-mp="2wuc2ui2tnx6x">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1050" data-mp="31v9kmtjx6p7w">{totalMsrp}</p>
            </div>
            <div data-id="element-1051" data-mp="36t1kqaupxhn6">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1052" data-mp="36rgpsk4hrwos">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1053" data-mp="2e55kiw5xx7ln">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-1054" data-mp="2j379tytym35k">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-1055" data-mp="1qerypndy84zi">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-1056" data-mp="1ve4ifz9iwvxv">
          <div className="flex items-center gap-2" data-id="element-1057" data-mp="1nu3r3y0cey10">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1058" data-mp="1iw28tfaqr2ql" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-1059" data-mp="1itzbea79e7bu">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-1060" data-mp="1dysa191gpeq3">Total MSRP value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-1061" data-mp="18xcn1bfq4y52">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-1062" data-mp="14xltx12q65xd">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-1063" data-mp="104sla244ydg0">
            <div className="flex items-start gap-2" data-id="element-1064" data-mp="v67uetarx648">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1065" data-mp="v6r3rlmcym6n" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-1066" data-mp="q97oqjhu65dn">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1067" data-mp="253nudd23u9cw">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1068" data-mp="2a2r2559gytok">
            <div className="flex items-center gap-2" data-id="element-1069" data-mp="1v4f3wu6g7cva">
              <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1070" data-mp="204b7wtf5b4au" />
              <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1071" data-mp="2064iukwskaon">Inventory submissions</h2>
            </div>
            {pageCount > 1 && <nav className="flex items-center gap-1.5 text-[13px]" aria-label="Submission pages" data-id="element-1072" data-mp="1r4erff2wtbqv">
                <button type="button" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Previous page" data-id="element-1073" data-mp="1cg1r65wsh6qk">
                  <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1074" data-mp="js8s3uipkfe8" />
                </button>
                <span className="tabular-nums text-ink/60" data-id="element-1075" data-mp="xs0eojnaj43u">
                  Page <span className="font-semibold text-ink" data-id="element-1076" data-mp="xtbg4ct9jzrs">{currentPage}</span> of {pageCount}
                </span>
                <button type="button" onClick={() => setPage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Next page" data-id="element-1077" data-mp="sv1i7ug5ml2h">
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1078" data-mp="1qhqqun5d3vxb" />
                </button>
              </nav>}
            <p className="ml-auto text-[13px] text-ink/50" data-id="element-1079" data-mp="1lfhztwc1v3xu">{rows.length.toLocaleString('en-US')} submissions</p>
          </div>
          <div className="max-h-[420px] overflow-auto" data-id="element-1080" data-mp="1t1hv8a6gf19t">
            <table className="w-full min-w-[700px] text-left text-sm" data-id="element-1081" data-mp="1t0zscupadosx">
              <thead className="sticky top-0 z-10 bg-cream text-xs text-ink/50" data-id="element-1082" data-mp="27xh3bktkd1ey">
                <tr data-id="element-1083" data-mp="22yvxn5vjlpj4">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1084" data-mp="230qmuwduk3t0">
                    <span className="flex items-center gap-2.5" data-id="element-1085" data-mp="1f91rt89n518d">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1086" data-mp="1aaruos4cvqk4" />
                      <SortButton label="Uploaded" sortKey="uploaded" sort={sort} onSort={toggleSort} data-id="element-1087" data-mp="1oze8gpec4a98" />
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1088" data-mp="1k7d1nizxrbql"><SortButton label="Batch" sortKey="batch" sort={sort} onSort={toggleSort} data-id="element-1089" data-mp="wj3fgfyt8lyx" /></th>
                  {allBrands && <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1090" data-mp="rl20a64i96sq"><SortButton label="Brand" sortKey="brand" sort={sort} onSort={toggleSort} data-id="element-1091" data-mp="ri60ec93k81n" /></th>}
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1092" data-mp="335j02m4mlzn0"><SortButton label="Unit count" sortKey="units" sort={sort} onSort={toggleSort} align="right" data-id="element-1093" data-mp="37zdou3z394sy" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1094" data-mp="2tb0qj0cj39va"><SortButton label="MSRP value" sortKey="msrp" sort={sort} onSort={toggleSort} align="right" data-id="element-1095" data-mp="2y7r8xubt2hjv" /></th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1096" data-mp="3lz4yjsiknz15"><SortButton label="Estimated recovery" sortKey="recovery" sort={sort} onSort={toggleSort} align="right" data-id="element-1097" data-mp="3qxq2o30y32uv" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1098" data-mp="3czrph0yd1cgy">
                {pageRows.length > 0 ? pageRows.map(row => {
                  const checked = isSelected(row);
                  return <tr key={row.id} className={checked ? '' : 'opacity-45'} data-id="element-1099" data-mp="21kmk1p6ugztn">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1100" data-mp="25l6jizew4fiy">
                        <span className="flex items-center gap-2.5" data-id="element-1101" data-mp="c0l8ua633a43">
                          <SelectBox checked={checked} onChange={() => toggleBatch(row)} label={`Include ${row.name} in totals`} data-id="element-1102" data-mp="71rzmtxty59q" />
                          {row.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1103" data-mp="3th35pqda6y17">{row.name}</th>
                      {allBrands && <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-1104" data-mp="3pdy924fvtqkh">{row.brand}</td>}
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1105" data-mp="3kj1170uc9n96">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1106" data-mp="3aja3kqahes4v">{row.msrp && row.msrp > 0 ? currency(row.msrp) : '—'}</td>
                      <td className="px-5 py-4 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-1107" data-mp="1dpyl8w55f1p1">{row.estimatedRecovery && row.estimatedRecovery > 0 ? currency(row.estimatedRecovery) : '—'}</td>
                    </tr>;
                }) : <tr data-id="element-1108" data-mp="1l9hjuaqvl3iq">
                    <td colSpan={allBrands ? 6 : 5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1109" data-mp="1l8y3gygvin6t">No inventory submissions yet.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {nisoloActive && <section className="mt-5" aria-label="AI insights" data-id="element-1110" data-mp="yav53dg3358t">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter((submission, index) => !deselected.includes(`Nisolo::${index}::${submission.name}`))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1111" data-mp="12avhksjmh6d8" />
        </section>}
        </>}
    </div>;
}
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left'
}: {
  label: string;
  sortKey: SortKey;
  sort: {
    key: SortKey;
    direction: 'asc' | 'desc';
  };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink ${active ? 'text-ink' : 'text-ink/50'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1112" data-mp="3rnwkls16x2oj">
      {label}
      <ChevronUpIcon className={`h-3 w-3 shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-0'} ${active && sort.direction === 'desc' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1113" data-mp="jd25an8ve5il" />
    </button>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1114" data-mp="124l5kmbif5gx">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1115" data-mp="1lotoe2hbglm4" />}
    </button>;
}